import Box from "@mui/material/Box";
import { cleanProps } from "../internal/cleanProps";
import { tokens } from "../tokens";
import { Icon } from "./Icon";
import { PortalPanelBody } from "./PortalPanelBody";
import type { PortalPanelProps } from "../types";

/**
 * Containment note (types.ts): no upstream generic is involved — a plain
 * native `button` plus `Box`.
 *
 * The trigger is a 44×44 (`tokens.interactionState.targetSize.standard`)
 * target carrying `aria-expanded`/`aria-controls` (WCAG 2.2 AA SC 4.1.2)
 * and a visually-hidden accessible name — `Icon`'s own `title` is
 * deliberately NOT supplied (that would give the icon itself a SECOND,
 * redundant accessible name via `role="img"`; this button already
 * supplies one, so the icon inside it stays decorative, exactly as
 * `Icon`'s own doc comment's "decorative by default" describes).
 *
 * Without `children` it renders the trigger alone; the caller places a
 * `PortalPanelBody` with the same `id` where the content may go (REQ-006).
 */
/** H2 04-05-GuidedExposures: each help trigger's icon, padding, margin and width. The question
 * trigger is H2's `legendNode` button, 34 by 44px; the others are 44 by 44px. */
const TRIGGERS = {
  lg: { icon: 28, padding: "10px", margin: "-10px 0 -10px 4px", minWidth: 44, justifySelf: undefined, color: undefined },
  md: { icon: 22, padding: "10px 6px", margin: "-10px -4px -10px 0", minWidth: 34, justifySelf: undefined, color: undefined },
  sm: { icon: 18, padding: "10px", margin: 0, minWidth: 44, justifySelf: "end", color: undefined },
  // H2's field-label trigger sets its own muted colour.
  field: { icon: 22, padding: "10px", margin: "-10px 0 -10px -2px", minWidth: 44, justifySelf: undefined, color: tokens.colour.neutral.inkMuted },
  title: { icon: 22, padding: "10px", margin: "-10px 0 -10px 4px", minWidth: 44, justifySelf: undefined, color: undefined },
} as const;

export function PortalPanel({ id, triggerLabel, expanded, onToggle, children, testId, size = "lg" }: Readonly<PortalPanelProps>) {
  const panelId = `${id}-panel`;
  const trigger = TRIGGERS[size];

  return (
    <Box component="span" sx={{ justifySelf: trigger.justifySelf }} {...cleanProps({ "data-testid": testId })}>
      <Box
        component="button"
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={panelId}
        sx={{
          appearance: "none",
          background: "none",
          border: "none",
          // ADR-20261102-h2fd (AC-12): H2's help triggers; the negative margin keeps the line
          // height of the text each follows.
          padding: trigger.padding,
          margin: trigger.margin,
          // H2 leaves the button's own colour to the browser; only the icon is muted.
          color: trigger.color ?? tokens.colour.h2.buttonText,
          "&:disabled": { color: tokens.colour.h2.buttonTextDisabled },
          minWidth: trigger.minWidth,
          minHeight: tokens.interactionState.targetSize.standard,
          display: "inline-flex",
          verticalAlign: "middle",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          "&:focus-visible": {
            outline: `${tokens.interactionState.focusRing.width}px solid ${tokens.interactionState.focusRing.colourOnLight}`,
            outlineOffset: tokens.interactionState.focusRing.offset,
          },
        }}
      >
        <Icon name="help" tone="muted" size={trigger.icon} />
        <Box
          component="span"
          sx={{
            position: "absolute",
            width: "1px",
            height: "1px",
            padding: 0,
            margin: "-1px",
            overflow: "hidden",
            clip: "rect(0, 0, 0, 0)",
            whiteSpace: "nowrap",
            border: 0,
          }}
        >
          {triggerLabel}
        </Box>
      </Box>
      {children !== undefined ? (
        <PortalPanelBody id={id} expanded={expanded}>
          {children}
        </PortalPanelBody>
      ) : null}
    </Box>
  );
}
